import React from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';

import ApplicantsTable from 'components/common/ApplicantsTable';
import ReusableTable from 'components/common/ReusableTable';
import ReusableSnackbar from 'components/common/ReusableSnackbar';
import ReusableTextFieldValidator from 'components/common/ReusableTextFieldValidator';
import { isFieldEmpty, fields } from 'utils/validation';
import history from 'wrappers/history';
import styles from './style.module.scss';

class ClassesPage extends React.Component {
  constructor(props) {
    super(props);

    if (!this.props.isLoggedIn) {
      history.push('/login');
    } else if (!this.props.isAdmin) {
      history.push('/forbidden');
    }

    this.state = {
      nameError: false,
      snackbarOpen: false,
      snackbarVariant: 'success',
      snackbarMessage: '',
      applicants: [],
      applicantsTableOpen: false,
      columns: [
        {
          title: 'Name',
          field: 'name',
          tooltip: 'The name of the math class from which students are recruited.',
          editComponent: (name) => (
            <ReusableTextFieldValidator
              text={name}
              onChange={(event) => name.onChange(event.target.value)}
              regExp={fields.className.regExp}
              error={this.state.nameError}
              onError={(error) => this.setState({ nameError: error })}
            />
          ),
        },
        {
          title: '#Volunteers',
          editable: 'never',
          customSort: (a, b) => this.numberOfApplicants(a) - this.numberOfApplicants(b),
          render: mathClass => (
            <div className={styles['applicant-button']}>
              <Button onClick={() => this.openApplicantsTable(mathClass)}>
                {this.numberOfApplicants(mathClass)}
              </Button>
            </div>
          ),
        },
        {
          title: 'Active',
          field: 'active',
          type: 'boolean',
          tooltip: 'Students are only able to sign up with classes that are marked as active.',
        },
        {
          title: 'Approved',
          field: 'approved',
          type: 'boolean',
          tooltip: 'Only approved classes show up in the list of classes for students to select from.',
        },
      ],
      options: {
        filtering: true,
        padding: 'dense',
        maxBodyHeight: 600,
        selection: true,
        hideDeleteButton: true,
      },
      editable: {
        isEditable: () => true,
        isDeletable: () => false,
        onRowAdd: (newClass) => new Promise((resolve, reject) => {
          const errorMessage = this.validateFields(newClass);
          if (errorMessage === '') {
            this.props.createClass(newClass);
            this.setState({
              snackbarOpen: true,
              snackbarVariant: 'success',
              snackbarMessage: `Class ${newClass.name} successfully added.`,
            });
            resolve();
          } else {
            this.setState({
              snackbarOpen: true,
              snackbarVariant: 'error',
              snackbarMessage: errorMessage,
            });
            reject();
          }
        }),
        onRowUpdate: (updatedClass) => new Promise((resolve, reject) => {
          const errorMessage = this.validateFields(updatedClass);
          if (errorMessage === '') {
            this.props.updateClass(updatedClass);
            this.setState({
              snackbarOpen: true,
              snackbarVariant: 'success',
              snackbarMessage: `Class ${updatedClass.name} successfully updated.`,
            });
            resolve();
          } else {
            this.setState({
              snackbarOpen: true,
              snackbarVariant: 'error',
              snackbarMessage: errorMessage,
            });
            reject();
          }
        }),
        onRowDelete: () => new Promise((resolve) => {
          resolve();
        }),
      },
      actions: [
        {
          icon: 'replay',
          tooltip: 'Refetch Data',
          isFreeAction: true,
          onClick: () => this.props.getClasses(),
        },
        {
          icon: 'save_alt',
          tooltip: 'Export',
          isFreeAction: true,
          onClick: () => {
            this.props.downloadClasses();
            this.setState({
              snackbarOpen: true,
              snackbarVariant: 'info',
              snackbarMessage: 'Download in progress.',
            });
          },
        },
      ],
    };
  }

  componentDidMount() {
    if (this.props.year === '') {
      this.props.getYear();
    }
    if (this.props.classes.length === 0) {
      this.props.getClasses();
    }
    if (this.props.applicants.length === 0) {
      this.props.getApplicants();
    }
  }

  numberOfApplicants = (mathClass) => {
    let numberOfApplicants = 0;
    this.props.applicants.filter(applicant => applicant.year === this.props.year).forEach(applicant => {
      if (applicant.math_class && applicant.math_class.id === mathClass.id) {
        ++numberOfApplicants;
      }
    });
    return numberOfApplicants;
  };

  openApplicantsTable = (mathClass) => {
    const mathClassApplicants = [];
    this.props.applicants.forEach(applicant => {
      if (applicant.math_class && applicant.math_class.id === mathClass.id) {
        mathClassApplicants.push(applicant);
      }
    });
    this.setState({ applicantsTableOpen: true, applicants: mathClassApplicants });
  };

  closeApplicantsTable = () => {
    this.setState({ applicantsTableOpen: false });
  };

  validateFields = (mathClass) => {
    let errorMessage = '';
    if (this.state.nameError || isFieldEmpty(mathClass.name)) {
      errorMessage += fields.className.errorMessage;
      this.setState({
        nameError: true,
      });
    }

    return errorMessage;
  };

  handleSnackbarClose = () => {
    this.setState({
      snackbarOpen: false,
    });
  };

  render() {
    return (
      <div id="maincontent">
        <div className="wdn-band">
          <div className="wdn-inner-wrapper">
            <div className={styles['table-container']}>
              <ReusableTable
                title="Math Classes"
                columns={this.state.columns}
                data={this.props.classes}
                options={this.state.options}
                editable={this.state.editable}
                isLoading={this.props.classesLoading || this.props.applicantsLoading}
                actions={this.state.actions}
              />
              <ReusableSnackbar
                open={this.state.snackbarOpen}
                onClose={this.handleSnackbarClose}
                variant={this.state.snackbarVariant}
                message={this.state.snackbarMessage}
              />
              <Dialog
                fullWidth
                maxWidth="lg"
                open={this.state.applicantsTableOpen}
                onClose={this.closeApplicantsTable}
              >
                <ApplicantsTable
                  loading={this.props.classesLoading || this.props.applicantsLoading}
                  applicants={this.state.applicants}
                  year={this.props.year}
                  eventDates={this.props.eventDates}
                  getYear={this.props.getYear}
                  getEventDates={this.props.getEventDates}
                />
              </Dialog>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

ClassesPage.propTypes = {
  classes: PropTypes.array.isRequired,
  classesLoading: PropTypes.bool.isRequired,
  createClass: PropTypes.func.isRequired,
  updateClass: PropTypes.func.isRequired,
  getClasses: PropTypes.func.isRequired,
  downloadClasses: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  applicants: PropTypes.array.isRequired,
  applicantsLoading: PropTypes.bool.isRequired,
  getApplicants: PropTypes.func.isRequired,
  year: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
  getYear: PropTypes.func.isRequired,
  eventDates: PropTypes.array.isRequired,
  getEventDates: PropTypes.func.isRequired,
};

export default ClassesPage;
